"use client";

import { createContext, useContext, useEffect } from 'react';
import { siteConfig } from '@/config/site';

interface ThemeContextType {
  config: typeof siteConfig;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Apply theme colors to CSS custom properties
    const root = document.documentElement;
    
    // Apply color variables
    Object.entries(siteConfig.theme.colors).forEach(([key, value]) => {
      const cssVar = `--${key.replace(/([A-Z])/g, '-$1').toLowerCase()}`;
      root.style.setProperty(cssVar, value);
    });

    // Apply border radius variables
    Object.entries(siteConfig.theme.borderRadius).forEach(([key, value]) => {
      const cssVar = `--radius-${key}`;
      root.style.setProperty(cssVar, value);
    });

    // Apply font variables from config so updates are centralized
    const headingFonts = [
      siteConfig.fonts.heading.family,
      ...(siteConfig.fonts.heading.fallbacks || []),
    ]
      .filter(Boolean)
      .join(', ');
    const bodyFonts = [
      siteConfig.fonts.body.family,
      ...(siteConfig.fonts.body.fallbacks || []),
    ]
      .filter(Boolean)
      .join(', ');
    // Do NOT override Next.js font loader variables; provide optional user overrides
    if (headingFonts) root.style.setProperty('--font-heading-user', headingFonts);
    if (bodyFonts) root.style.setProperty('--font-body-user', bodyFonts);
  }, []);

  return (
    <ThemeContext.Provider value={{ config: siteConfig }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
} 